import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

dayjs.extend(isoWeek);

/**
 * Bộ lọc thời gian của màn hình lịch hẹn chạy theo NGÀY HẸN (`scheduled_at`), lấy trọn
 * kỳ chứ không cắt ở hôm nay vì lịch hẹn nằm cả ở tương lai.
 *
 * API /appointments chưa có tham số lọc theo `scheduled_at` (date_from/date_to của nó
 * lọc theo ngày tạo), nên phần lọc phải làm ở giao diện — dùng chung giữa bảng danh
 * sách và lịch để hai nơi luôn ra cùng một tập dữ liệu.
 */

export type ScheduledPeriod = "yesterday" | "date" | "tomorrow" | "week" | "month" | "year";

export interface ScheduledRange {
    from: Dayjs;
    to: Dayjs;
}

/** Chế độ xem lịch hợp lý nhất cho từng kỳ */
export const CALENDAR_VIEW_BY_PERIOD: Record<string, "day" | "week" | "month"> = {
    yesterday: "day",
    date: "day",
    tomorrow: "day",
    week: "week",
    month: "month",
    year: "month",
};

export const getScheduledRange = (period?: string): ScheduledRange | null => {
    if (!period) return null;
    const now = dayjs();

    if (period === "yesterday") {
        const d = now.subtract(1, "day");
        return { from: d.startOf("day"), to: d.endOf("day") };
    }
    if (period === "tomorrow") {
        const d = now.add(1, "day");
        return { from: d.startOf("day"), to: d.endOf("day") };
    }

    // Tuần tính từ Thứ 2 (isoWeek) cho khớp cách hiển thị lịch trong hệ thống
    const unitMap: Record<string, dayjs.OpUnitType> = {
        date: "day",
        week: "isoWeek" as dayjs.OpUnitType,
        month: "month",
        year: "year",
    };
    const unit = unitMap[period];
    if (!unit) return null;
    return { from: now.startOf(unit), to: now.endOf(unit) };
};

/** Ngày hẹn có nằm trong kỳ đang lọc không */
export const isScheduledInRange = (scheduledAt: string | null | undefined, range: ScheduledRange) => {
    if (!scheduledAt) return false;
    const at = dayjs(String(scheduledAt).replace(" ", "T"));
    return at.isValid() && !at.isBefore(range.from) && !at.isAfter(range.to);
};
